import React, { useState, useMemo } from 'react';

/**
 * SearchableMultiSelect - A reusable component for selecting multiple items from a large list.
 * 
 * @param {string} label - Display label for the field
 * @param {Array} options - Array of { value, label } objects
 * @param {Array} selectedValues - Array of currently selected values
 * @param {Function} onChange - Callback function when selection changes
 * @param {string} error - Error message to display
 * @param {boolean} loading - Loading state
 * @param {string} placeholder - Search input placeholder
 * @param {string} emptyMessage - Message to show when no options match search
 * @param {boolean} required - Whether the field is required
 */
const SearchableMultiSelect = ({
  label,
  options = [],
  selectedValues = [],
  onChange,
  error,
  loading = false,
  placeholder = "Search options...",
  emptyMessage = "No matching options found.",
  required = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = useMemo(() => {
    let filtered = options;
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = options.filter(opt => 
        opt.label.toLowerCase().includes(lowerSearch) || 
        (opt.subtitle && opt.subtitle.toLowerCase().includes(lowerSearch))
      );
    }

    // Sort: Selected items first
    return [...filtered].sort((a, b) => {
      const aSelected = selectedValues.includes(a.value);
      const bSelected = selectedValues.includes(b.value);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });
  }, [options, searchTerm, selectedValues]);

  const handleToggle = (value, isChecked) => {
    let newValues;
    if (isChecked) {
      newValues = [...selectedValues, value];
    } else {
      newValues = selectedValues.filter(v => v !== value);
    }
    onChange(newValues);
  };

  const handleSelectAll = (e) => {
    e.preventDefault();
    const allFilteredValues = filteredOptions.map(opt => opt.value);
    const newValues = Array.from(new Set([...selectedValues, ...allFilteredValues]));
    onChange(newValues);
  };

  const handleClearSelection = (e) => {
    e.preventDefault();
    onChange([]);
  };

  // Styles
  const containerStyle = {
    marginBottom: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  };

  const labelStyle = {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--text-primary)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  };

  const searchInputStyle = {
    width: '100%',
    padding: '10px 12px',
    paddingLeft: '36px',
    fontSize: '14px',
    border: '1px solid var(--border)',
    borderRadius: '8px 8px 0 0',
    background: 'var(--surface)',
    color: 'var(--text-primary)',
    outline: 'none',
    transition: 'border-color 0.2s ease'
  };

  const listContainerStyle = {
    maxHeight: '220px',
    overflowY: 'auto',
    border: `1px solid ${error ? '#ef4444' : 'var(--border)'}`,
    borderTop: 'none',
    borderRadius: '0 0 8px 8px',
    background: 'var(--surface)'
  };

  const optionStyle = (isSelected) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    cursor: 'pointer',
    borderBottom: '1px solid var(--border)',
    background: isSelected ? 'rgba(59, 130, 246, 0.08)' : 'transparent',
    transition: 'background 0.15s ease',
    margin: 0,
    '&:last-child': {
      borderBottom: 'none'
    }
  });

  const badgeStyle = {
    fontSize: '11px',
    background: 'var(--primary)',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '10px',
    fontWeight: '700'
  };

  return (
    <div style={containerStyle}>
      {label && (
        <div style={labelStyle}>
          <span>
            {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
          </span>
          {selectedValues.length > 0 && (
            <span style={badgeStyle}>{selectedValues.length} selected</span>
          )}
        </div>
      )}

      <div style={{ position: 'relative' }}>
        <div style={{
          position: 'absolute',
          left: '12px',
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'var(--text-secondary)',
          display: 'flex',
          alignItems: 'center',
          pointerEvents: 'none'
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
        </div>
        <input
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={searchInputStyle}
          onFocus={(e) => e.target.style.borderColor = 'var(--primary)'}
          onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
        />
      </div>

      <div style={listContainerStyle}>
        {loading ? (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '14px' }}>
            Loading options...
          </div>
        ) : filteredOptions.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '14px' }}>
             {emptyMessage}
          </div>
        ) : (
          filteredOptions.map((option, index) => {
            const isSelected = selectedValues.includes(option.value);
            return (
              <label 
                key={option.value} 
                style={optionStyle(isSelected)}
              >
                <input 
                  type="checkbox" 
                  checked={isSelected} 
                  onChange={(e) => handleToggle(option.value, e.target.checked)} 
                  style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                  <span style={{ 
                    fontSize: '14px', 
                    color: isSelected ? 'var(--primary)' : 'var(--text-primary)',
                    fontWeight: isSelected ? '600' : '500',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {option.label}
                  </span>
                  {option.subtitle && (
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {option.subtitle}
                    </span>
                  )}
                </div>
              </label>
            );
          })
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 2px' }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button 
            onClick={handleSelectAll}
            style={{ border: 'none', background: 'none', color: 'var(--primary)', fontSize: '12px', fontWeight: '600', cursor: 'pointer', padding: 0 }}
          >
            Select All Filtered
          </button>
          <button 
            onClick={handleClearSelection}
            style={{ border: 'none', background: 'none', color: '#ef4444', fontSize: '12px', fontWeight: '600', cursor: 'pointer', padding: 0 }}
          >
            Clear All
          </button>
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          Showing {filteredOptions.length} of {options.length}
        </div>
      </div>

      {error && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '2px' }}>{error}</div>}
    </div>
  );
};

export default SearchableMultiSelect;
